import { useMemo } from "react";
import { buildQrModuleGrid } from "@laundrypro/shared";

/**
 * The ZATCA QR, drawn as inline SVG.
 *
 * SVG rather than canvas because this has to survive PRINT. A canvas is a
 * raster at one fixed resolution: at 300dpi on A4 it prints soft, and on a
 * thermal head it prints muddy — and a QR that will not scan is not a QR. SVG
 * modules stay crisp at any output resolution, and the browser's own print
 * pipeline rasterises them at the device's real DPI.
 *
 * The module GRID itself comes from `buildQrModuleGrid` in `@laundrypro/shared`
 * — the exact function the server-side PDF template also calls. That is what
 * makes "the downloaded PDF's QR matches the screen's QR" true by
 * construction: one function, called from two runtimes, not two
 * implementations that happen to agree today.
 *
 * The payload is passed in EXACTLY as stored on the invoice. Nothing here
 * re-derives it, so a reprint years later encodes the same bytes it did on the
 * day of issue.
 */
export function QrCode({
  payload,
  size = 108,
  className = "",
  title,
}: {
  /** Base64 TLV, straight from `invoice.zatcaQrPayload`. */
  payload: string;
  /** Rendered edge length in CSS pixels. */
  size?: number;
  className?: string;
  title?: string;
}) {
  const grid = useMemo(() => buildQrModuleGrid(payload), [payload]);

  return (
    <svg
      viewBox={`0 0 ${grid.span} ${grid.span}`}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title ?? "ZATCA QR"}
      shapeRendering="crispEdges"
    >
      {/* An explicit white ground, because the quiet zone must stay white even
          if the surrounding card is tinted — and because a transparent QR
          printed onto coloured stock will not scan. */}
      <rect width={grid.span} height={grid.span} fill="#ffffff" />
      <path d={grid.path} fill="#000000" />
    </svg>
  );
}
